import React, { useId, useState } from "react";
import { X, UserPlus, Trash2 } from "lucide-react";
import { Dialog } from "../ui/Dialog";
import { List } from "../../types";
import { useListMemberDirectory } from "../../hooks/queries/useListMemberDirectory";
import { useInviteListMember } from "../../hooks/mutations/useInviteListMember";
import { useRemoveListMember } from "../../hooks/mutations/useRemoveListMember";
import { useUnshareList } from "../../hooks/mutations/useUnshareList";

interface ManageSharingDialogProps {
  list: List;
  onClose: () => void;
}

/**
 * The Owner's manage panel (build plan Session F, item 3): invite by email,
 * see every Member and unanswered Invitation, remove one, or stop sharing
 * the List entirely. Only ever opened from a List the viewer owns —
 * list_member_directory() enforces that server-side too.
 */
const ManageSharingDialog: React.FC<ManageSharingDialogProps> = ({ list, onClose }) => {
  const titleId = useId();
  const [email, setEmail] = useState("");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [confirmingUnshare, setConfirmingUnshare] = useState(false);

  const { data: members = [], isLoading } = useListMemberDirectory(list.id, true);
  const invite = useInviteListMember();
  const remove = useRemoveListMember();
  const unshare = useUnshareList();

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    try {
      await invite.mutateAsync({ listId: list.id, email: email.trim() });
      setEmail("");
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : "Couldn't send that invitation.");
    }
  };

  return (
    <Dialog
      onClose={onClose}
      labelledBy={titleId}
      backdropClassName="z-30"
      panelClassName="w-full max-w-md max-h-[90vh] overflow-y-auto"
    >
      <div className="flex items-center justify-between mb-4">
        <h2 id={titleId} className="text-lg font-semibold text-warm-text-primary">
          {list.emoji} {list.name}
        </h2>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-warm-text-secondary hover:text-warm-text-primary transition-colors p-1 -m-1"
        >
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleInvite} className="mb-5">
        <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
          Invite by email
        </label>
        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            required
            className="flex-1 min-w-0 bg-warm-bg-elevated border border-warm-border rounded-soft px-3 py-2 text-sm text-warm-text-primary placeholder:text-warm-text-tertiary focus:outline-none focus:ring-2 focus:ring-warm-blue"
          />
          <button
            type="submit"
            disabled={invite.isPending || !email.trim()}
            className="flex-shrink-0 px-3 py-2 bg-warm-blue hover:bg-warm-blue-dark text-white rounded-soft text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <UserPlus size={14} />
            Invite
          </button>
        </div>
        {inviteError && <p className="text-xs text-red-500 mt-2">{inviteError}</p>}
      </form>

      <div>
        <label className="block text-xs font-medium text-warm-text-tertiary mb-2 uppercase tracking-wider">
          Members &amp; invitations
        </label>
        {isLoading && <p className="text-sm text-warm-text-tertiary">Loading…</p>}
        {!isLoading && members.length === 0 && (
          <p className="text-sm text-warm-text-tertiary">Nobody's been invited yet.</p>
        )}
        <div className="rounded-xl border border-warm-border bg-warm-bg-elevated divide-y divide-warm-border overflow-hidden">
          {members.map((m) => (
            <div key={m.user_id} className="px-3 py-2.5 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm text-warm-text-primary truncate">{m.email}</div>
                <div className="text-xs text-warm-text-tertiary">
                  {m.status === "accepted" ? "Member" : "Invited"}
                </div>
              </div>
              <button
                onClick={() => remove.mutate({ listId: list.id, userId: m.user_id })}
                aria-label={m.status === "accepted" ? `Remove ${m.email}` : `Cancel invitation to ${m.email}`}
                className="flex-shrink-0 px-2.5 py-1 rounded-md text-xs text-warm-text-tertiary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
              >
                {m.status === "accepted" ? "Remove" : "Cancel"}
              </button>
            </div>
          ))}
        </div>
      </div>

      {members.length > 0 && (
        <div className="mt-6 pt-4 border-t border-warm-border">
          {confirmingUnshare ? (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-warm-text-secondary">
                Every Member loses this list. It becomes private again.
              </p>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setConfirmingUnshare(false)}
                  className="px-3 py-1.5 text-sm text-warm-text-secondary hover:text-warm-text-primary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    unshare.mutate(list.id);
                    setConfirmingUnshare(false);
                  }}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-soft text-sm transition-colors"
                >
                  Stop sharing
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setConfirmingUnshare(true)}
              className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 transition-colors"
            >
              <Trash2 size={14} />
              Stop sharing this list
            </button>
          )}
        </div>
      )}
    </Dialog>
  );
};

export default ManageSharingDialog;
